import { useState, useMemo, useCallback, useEffect } from 'react';
import { isEmpty } from 'lodash-es';
import {
  store,
} from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import {
  COPILOT_EXTERNAL_QUERY_CONFIG_KEY,
} from '@/pages/aigc/pages/Agent/config/constant';
import type { ICopilotExternalQueryConfig } from './useCopilotOpenBridge';
import type { Conversation } from '@/pages/aigc/pages/Agent/ChatPage/types/history';

/** store 中实际存储的结构：原始配置 + 可选的 cleared 快照 */
type TStoreConfig = (ICopilotExternalQueryConfig & {
  /** Copilot Chat 消费后，将原始值暂存于此，供 Home / ChatPage 恢复 */
  cleared?: ICopilotExternalQueryConfig;
}) | null;

type TCopilotExternalConfig = ICopilotExternalQueryConfig | null;

interface UseExternalQueryConfigOptions {
  /** 当前会话列表（用于提取 historyBiDashboardFieldName） */
  conversations?: Conversation[];
  /** 当前激活的页签 key，传入后仅 aiAgent 页签展示，不传则始终展示 */
  activeTab?: string;
  /** 是否从 cleared 恢复数据并彻底清空 store（Home 传 true：恢复 + 清空） */
  clearStore?: boolean;
  /** 仅清空 store 但不恢复数据（ChatPage 落地页传 true：只清空、不显示） */
  clearOnly?: boolean;
}

interface ExternalQueryInputProps {
  value: string;
  copilotRefTagText: string | undefined;
  copilotRefTagShowClose: boolean;
  copilotRefTagHideArrow?: boolean;
  copilotRefTagImage?: string;
  onCopilotRefTagDismiss?: () => void;
}

/** 读取并消费 externalQueryConfig，计算 QAInput props */
export function useExternalQueryConfig(options: UseExternalQueryConfigOptions = {}) {
  const { conversations, activeTab, clearStore, clearOnly } = options;

  // ===== State =====
  const [externalQueryConfig] = useState<TCopilotExternalConfig>(() => {
    const raw = store.getItem<TStoreConfig>(COPILOT_EXTERNAL_QUERY_CONFIG_KEY, null);
    if (!raw) return null;

    if (clearOnly) {
      // ChatPage 落地页：只清空 store，不恢复任何数据
      store.setItem(COPILOT_EXTERNAL_QUERY_CONFIG_KEY, null);
      return null;
    }

    if (clearStore) {
      // Home：从 cleared 恢复数据 → 彻底清空；opinion_pc / aigallery_report 不带到 Home
      const restored = raw.cleared ?? null;
      store.setItem(COPILOT_EXTERNAL_QUERY_CONFIG_KEY, null);
      if (restored?.form === 'opinion_pc' || restored?.form === 'aigallery_report') {
        return null;
      }
      return restored;
    }

    // Copilot Chat：读取原始配置，消费后将原始值存入 cleared 供 Home 恢复
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cleared: clearedSnapshot, ...config } = raw;
    void clearedSnapshot;
    if (config.query || config.prefixTag || config.form) {
      store.setItem(COPILOT_EXTERNAL_QUERY_CONFIG_KEY, { cleared: config });
    }
    // 修复：已消费的数据（config 为空对象）应返回 null，避免后续逻辑误判为有效的外部查询配置
    // 否则 qaServerParams 中的 frontend_metadata 会被 {} 的 spread 覆盖
    return config.query || config.prefixTag || config.form ? config : null;
  });
  const [isRefTagDismissed, setIsRefTagDismissed] = useState(false);

  useEffect(() => {
    setIsRefTagDismissed(false);
  }, [externalQueryConfig?.prefixTag, externalQueryConfig?.form]);

  const dismissRefTag = useCallback(() => {
    setIsRefTagDismissed(true);
  }, []);

  const effectiveExternalQueryConfig = isRefTagDismissed ? null : externalQueryConfig;

  // ── 计算 QAInput 绑定的 props ──
  const externalQueryInputProps = useMemo<ExternalQueryInputProps>(() => {
    // 传了 activeTab：仅在 aiAgent 页签或已有对话时展示；未传则始终展示
    const showExternal = activeTab
      ? (activeTab === 'aiAgent' || !isEmpty(conversations))
      : true;

    // 从历史会话中提取 bi_dashboard 的 field_name 作为 copilotRefTagText fallback
    const historyBiDashboardFieldName = (() => {
      if (externalQueryConfig?.prefixTag) return undefined; // 外部配置优先
      const biMsg = conversations?.find(
        (conv: any) => conv?.frontend_metadata?.type === 'bi_dashboard' && conv?.frontend_metadata?.field_name,
      );
      return biMsg?.frontend_metadata?.field_name as string | undefined;
    })();

    // 计算输入框 value：prefixTag 加引用括号 + query
    const prefixTag = externalQueryConfig?.prefixTag;
    const query = externalQueryConfig?.query ?? '';
    const form = externalQueryConfig?.form;
    const prefixImage = externalQueryConfig?.prefixImage;

    // opinion_pc：不预填输入框，隐藏箭头，chip 左侧展示 prefixImage 缩略图
    const isOpinionPC = form === 'opinion_pc';
    // aigallery_report：标题走 ExternalRefCard 专用卡片，输入框只预填 query
    const isAIGalleryReport = form === 'aigallery_report';

    const formattedValue = showExternal && !isRefTagDismissed
      ? (isOpinionPC ? '' : (isAIGalleryReport ? query : (prefixTag ? `「${prefixTag}」${query}` : query)))
      : '';

    return {
      value: formattedValue,
      copilotRefTagText: showExternal && !isRefTagDismissed
        ? (prefixTag || historyBiDashboardFieldName)
        : undefined,
      // opinion_pc 新会话可关闭；其它来源仅在有对话历史时展示关闭按钮
      copilotRefTagShowClose: isOpinionPC || !isEmpty(conversations),
      copilotRefTagHideArrow: isOpinionPC,
      copilotRefTagImage: isOpinionPC ? prefixImage : undefined,
      onCopilotRefTagDismiss: dismissRefTag,
    };
  }, [activeTab, conversations, dismissRefTag, externalQueryConfig?.query, externalQueryConfig?.prefixTag, externalQueryConfig?.form, externalQueryConfig?.prefixImage, isRefTagDismissed]);

  return {
    /** 有效外部配置（关闭 chip 后为 null，提交时不透传） */
    externalQueryConfig: effectiveExternalQueryConfig,
    /** QAInput 绑定的 props：value / copilotRefTagText / copilotRefTagShowClose */
    externalQueryInputProps,
  };
}
